import { usePDFPrintHook } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../../../constant/Endpoint";
import { DeleteIcon } from "../../../../Icons/DeleteIcon";
import { classNames } from "../../../../utils/classNames";
import Button from "../../../Reusable/Button";
import DataTable from "../../../Reusable/Datatable";
import NACell from "../../../Reusable/Datatable/NACell";
import DeletePopupModal from "../../../Reusable/DeletePopupModal";
import TableActionButton from "../../../Reusable/TableActionButton";
import { AccessControl } from "@careinfo/permission-required";

const CashReceipt = (props: any) => {
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [searchParams, setSearchParms] = useSearchParams();
  const checkedAllItems = searchParams.get("selectAllItems") === "1";

  const [openDeletePopup, setOpenDeletePopup] = useState(false);

  const { handleDelete, isLoading } = props;
  const [deleteItem, setDeleteItem] = useState(null);

  const handleIndividualSelect = (value: string) => {
    const clonedParams = new URLSearchParams(searchParams);
    const selectedItems = clonedParams.get("selectedItems")?.split(",");
    if (!selectedItems || !selectedItems === undefined) {
      clonedParams.set("selectedItems", value);
      setSearchParms(clonedParams);
      return;
    }
    const itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        clonedParams.set("selectedItems", newSelectedItems);
      } else {
        clonedParams.delete("selectedItems");
      }
      clonedParams.set("selectAllItems", "0");
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      clonedParams.set("selectedItems", newSelectedItems);
    }
    setSearchParms(clonedParams);
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const clonedParams = new URLSearchParams(searchParams);
    const isSelected = clonedParams.get("selectAllItems");
    if (isSelected === "1") {
      clonedParams.set("selectAllItems", "0");
      clonedParams.delete("selectedItems");
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      clonedParams.set("selectAllItems", "1");
      clonedParams.set("selectedItems", selectedItems);
    }
    setSearchParms(clonedParams);
  };
  const columns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => {
            handleIndividualSelect(row?.original?.id);
          }}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      accessorKey: "voucher_date",
      header: "Date",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "miti",
      header: "Miti",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "seq_no",
      header: "Bill no.",
      cell: NACell,
    },
    { accessorKey: "party_account.name", header: "Party", cell: NACell },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row, getValue }: any) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      ),
    },
    {
      accessorKey: "narration",
      header: "Narration",
      cell: NACell,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row, getValue }) => (
        <p
          className={classNames(
            getValue() === "Draft" ? tw`bg-yellow-400` : tw`bg-[#32CD32]`,
            tw`px-2 py-1 text-center rounded-md`
          )}
        >
          {getValue()}
        </p>
      ),
    },
    {
      accessorKey: "id",
      header: "Action",
      cell: ({ row, getValue }: any) => {
        return (
          <div className={tw`flex gap-x-4 items-center`}>
            <AccessControl allowedPermissions={["can_edit_cash_receipt"]}>
              <TableActionButton editPath={`edit/${getValue()}`} />
            </AccessControl>
            <AccessControl allowedPermissions={["can_delete_cash_receipt"]}>
              <button
                type="button"
                onClick={() => {
                  setDeleteItem(row?.original?.id);
                  setOpenDeletePopup(true);
                }}
                className={tw`focus:outline-none w-6 h-6`}
              >
                <DeleteIcon color={"var(--primary)"} />
              </button>
            </AccessControl>
          </div>
        );
      },
    },
  ];

  const handlePrint = async () => {
    const selectedOrder = searchParams.get("selectedItems")?.split(",");
    if (!selectedOrder?.length) {
      toast.error("Please select at least one first...");
      return;
    }
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: selectedOrder,
      type: "Cash Receipt",
    };
    try {
      const res = await downloadPrint(url, formData);
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <div className={tw`space-y-4`}>
        <DataTable
          columns={columns}
          queryKey="cash-receipt"
          tableName="cash-receipt"
          url={APIS.BILL}
          params={{
            balance_type: "debit",
            type: "Cash Receipt",
          }}
          // customSearch={true}
          addNew={{
            path: "create",
            label: "Add New",
          }}
          actionTools={
            <>
              <div className={tw`flex  w-full items-center`}>
                <AccessControl allowedPermissions={["can_print_cash_receipt"]}>
                  <button
                    type="button"
                    onClick={() => handlePrint()}
                    disabled={isPrintLoading || false}
                    className={tw`px-6 rounded-md py-2 bg-primary text-white focus:outline-none`}
                  >
                    Print
                  </button>
                </AccessControl>
              </div>
            </>
          }
        />
      </div>
      <DeletePopupModal isOpen={openDeletePopup} setIsOpen={setOpenDeletePopup}>
        <div className={tw`flex flex-col justify-center items-center  h-fit`}>
          <p className={tw`text-lg font-semibold mb-4`}>
            Are your sure you want to delete ?
          </p>
          <div className={tw`mt-6 flex justify-center gap-x-6 items-center`}>
            <div className={tw`w-24`}>
              <Button
                label={"Yes"}
                isLoading={isLoading}
                onClick={() => handleDelete(deleteItem, setOpenDeletePopup)}
              />
            </div>
            <div>
              <button
                type="button"
                className={tw`w-full px-[24px] py-2 rounded  focus:outline-none bg-[#EEF5F4]`}
                onClick={() => setOpenDeletePopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </DeletePopupModal>
    </>
  );
};

export default CashReceipt;
